import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import usePageMeta from "@/hooks/usePageMeta";
import { categoryNameMap, topics } from "@/mocks/topics";

export default function TopicDetail() {
  const { slug } = useParams();
  const topic = topics.find((item) => item.slug === slug);

  const related = topic
    ? topics.filter((item) => item.categoryId === topic.categoryId && item.id !== topic.id).slice(0, 3)
    : [];

  usePageMeta({
    title: topic ? `${topic.title}｜看料档案深度专题` : "档案未找到｜看料档案",
    description: topic
      ? `${topic.excerpt} 看料档案从时间线、利益链与关键节点三个层面梳理事件内幕与细节，帮你看清背后逻辑。`
      : "该档案不存在或已被移动，请返回深挖专区浏览其他深度专题。",
    keywords: topic ? `${topic.tags.join(",")},看料黑料深挖,事件起底` : "看料档案",
    canonicalPath: topic ? `/topics/${topic.slug}` : "/topics",
    jsonLd: topic
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: topic.title,
              description: topic.excerpt,
              datePublished: topic.date,
              dateModified: topic.date,
              author: { "@type": "Organization", name: topic.author },
              publisher: { "@type": "Organization", name: "看料档案" },
              articleSection: categoryNameMap[topic.categoryId],
              keywords: topic.tags.join(","),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://kanliao-archive.example/" },
                { "@type": "ListItem", position: 2, name: "深挖专区", item: "https://kanliao-archive.example/topics" },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: topic.title,
                  item: `https://kanliao-archive.example/topics/${topic.slug}`,
                },
              ],
            },
          ],
        }
      : undefined,
  });

  if (!topic) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-3xl flex-col items-center gap-5 px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">档案未找到</h1>
          <p className="max-w-md text-sm leading-7 text-foreground-600">
            这篇档案不存在或已经被移动到其他栏目，你可以返回深挖专区继续浏览其他深度专题。
          </p>
          <Link
            to="/topics"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回深挖专区
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <article>
          <header className="relative border-b border-background-200 bg-background-100">
            <div className="paper-grain pointer-events-none absolute inset-0" />
            <div className="relative mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
              <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <Link to="/" className="transition-colors hover:text-primary-600">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line" />
                <Link to="/topics" className="transition-colors hover:text-primary-600">
                  深挖专区
                </Link>
                <i className="ri-arrow-right-s-line" />
                <Link
                  to={`/topics?category=${topic.categoryId}`}
                  className="transition-colors hover:text-primary-600"
                >
                  {categoryNameMap[topic.categoryId]}
                </Link>
              </nav>

              <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                    <i className="ri-price-tag-3-line" />
                    {categoryNameMap[topic.categoryId]}
                  </span>
                  <h1 className="mt-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                    {topic.title}
                  </h1>
                  <p className="mt-5 text-sm leading-8 text-foreground-600 md:text-base">
                    {topic.lead}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <i className="ri-user-3-line" />
                      {topic.author}
                    </span>
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <i className="ri-calendar-line" />
                      {topic.date}
                    </span>
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <i className="ri-time-line" />
                      阅读约 {topic.readTime} 分钟
                    </span>
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <i className="ri-fire-line text-primary-500" />
                      热度 {(topic.hot / 1000).toFixed(1)}k
                    </span>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="h-64 w-full overflow-hidden rounded-xl2 md:h-72">
                    <img
                      src={topic.cover}
                      alt={topic.title}
                      title={`${topic.title} - 看料档案深度专题`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-8">
                <div className="rounded-xl border border-accent-300 bg-accent-50 p-6">
                  <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-accent-900">
                    <i className="ri-list-check-2" />
                    本期要点
                  </h2>
                  <ul className="mt-4 flex flex-col gap-3">
                    {topic.takeaways.map((point) => (
                      <li key={point} className="flex gap-3 text-sm leading-7 text-foreground-700">
                        <i className="ri-arrow-right-s-fill mt-1 text-accent-600" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10 flex flex-col gap-10">
                  {topic.sections.map((section, index) => (
                    <section key={section.heading} id={`section-${index}`} className="scroll-mt-24">
                      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                        {section.heading}
                      </h2>
                      <div className="mt-4 flex flex-col gap-4">
                        {section.paragraphs.map((para) => (
                          <p key={para.slice(0, 24)} className="text-sm leading-8 text-foreground-700 md:text-[15px]">
                            {para}
                          </p>
                        ))}
                      </div>
                      {section.quote && (
                        <blockquote className="mt-6 border-l-2 border-primary-500 bg-background-100 px-5 py-4 font-heading text-base leading-8 text-foreground-800">
                          {section.quote}
                        </blockquote>
                      )}
                    </section>
                  ))}
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                  <span className="text-xs text-foreground-500">相关标签：</span>
                  {topic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/topics?q=${encodeURIComponent(tag)}`}
                      className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24">
                  <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                    <h2 className="font-heading text-sm font-semibold tracking-wider text-foreground-950">
                      档案目录
                    </h2>
                    <nav className="mt-4 flex flex-col">
                      {topic.sections.map((section, index) => (
                        <a
                          key={section.heading}
                          href={`#section-${index}`}
                          className="border-l border-background-200 py-2 pl-4 text-sm text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-600"
                        >
                          {section.heading}
                        </a>
                      ))}
                    </nav>
                  </div>

                  <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-6">
                    <h2 className="font-heading text-sm font-semibold tracking-wider text-foreground-950">
                      信息说明
                    </h2>
                    <p className="mt-3 text-xs leading-6 text-foreground-600">
                      本篇基于公开信息整理，正文中已区分「可核实信息」与「待核实推测」。如你掌握新的时间线节点，欢迎通过订阅表单提供线索。
                    </p>
                  </div>
                </div>
              </aside>
            </div>

            {related.length > 0 && (
              <section className="mt-16 border-t border-background-200 pt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  同栏目相关档案
                </h2>
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/topics/${item.slug}`}
                      className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
                    >
                      <div className="h-40 w-full overflow-hidden">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 看料档案`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-600">
                          {item.title}
                        </h3>
                        <p className="mt-3 line-clamp-2 flex-1 text-sm leading-6 text-foreground-600">
                          {item.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}